import Company from 'app/interfaces/Company';
import {
  NavigationConfiguration,
  ProductionDefaultDateConfiguration,
  SystemConfiguration,
  SystemModulesUpdate,
} from 'app/interfaces/Config';
import { ApiError } from 'app/lib/authFetch';

class ConfigService {
  private baseUrl: string;

  constructor(baseUrl: string) {
    this.baseUrl = baseUrl;
  }

  async get(refresh = false): Promise<SystemConfiguration | null> {
    try {
      let url = `${this.baseUrl}config`;
      refresh ? (url += '?refresh=' + refresh) : '';
      const response = await fetch(url, {
        method: 'GET',
      });

      if (response.status === 404) {
        return null;
      }

      if (!response.ok) {
        console.error('Failed to add asset');
        throw new Error('Failed to add asset');
      }

      return response.json();
    } catch (error) {
      console.error('Error adding asset:', error);
      throw error;
    }
  }

  async getCompany() {
    try {
      const url = `${this.baseUrl}company`;
      const response = await fetch(url, {
        method: 'GET',
      });

      if (!response.ok) {
        console.error('Failed to add asset');
        throw new Error('Failed to add asset');
      }

      return response.json();
    } catch (error) {
      console.error('Error adding asset:', error);
      throw error;
    }
  }

  async UpdateCompany(company: Company) {
    try {
      const url = `${this.baseUrl}company`;
      const response = await fetch(url, {
        method: 'PUT',
        body: JSON.stringify(company),
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        console.error('Failed to add asset');
        throw new Error('Failed to add asset');
      }
    } catch (error) {
      console.error('Error adding asset:', error);
      throw error;
    }
  }

  async updateModules(modules: SystemModulesUpdate, token: string): Promise<SystemConfiguration> {
    try {
      const url = `${this.baseUrl}config/Modules`;
      const response = await fetch(url, {
        method: 'PUT',
        body: JSON.stringify(modules),
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        const detail = await response.text();
        console.error('Failed to update system modules', detail);
        throw new Error(detail || 'Failed to update system modules');
      }

      return response.json();
    } catch (error) {
      console.error('Error updating system modules:', error);
      throw error;
    }
  }

  async updateNavigation(
    navigation: NavigationConfiguration | null,
    token?: string
  ): Promise<SystemConfiguration> {
    try {
      const url = `${this.baseUrl}config/Navigation`;
      const response = await fetch(url, {
        method: 'PUT',
        body: JSON.stringify(navigation),
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
      });

      if (!response.ok) {
        const detail = await response.text();
        console.error('Failed to update navigation', response.status, detail);
        throw new ApiError(detail || String(response.status), response.status);
      }

      return response.json();
    } catch (error) {
      console.error('Error updating navigation:', error);
      throw error;
    }
  }

  async updateProductionDefaultDate(
    value: ProductionDefaultDateConfiguration | null,
    token?: string
  ): Promise<SystemConfiguration> {
    try {
      const url = `${this.baseUrl}config/ProductionDefaultDate`;
      const response = await fetch(url, {
        method: 'PUT',
        body: JSON.stringify(value),
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
      });

      if (!response.ok) {
        const detail = await response.text();
        console.error(
          'Failed to update production default date',
          response.status,
          detail
        );
        throw new ApiError(detail || String(response.status), response.status);
      }

      return response.json();
    } catch (error) {
      console.error('Error updating production default date:', error);
      throw error;
    }
  }

  async getLanguages(): Promise<string[]> {
    try {
      const url = `${this.baseUrl}config/Languages`;
      const response = await fetch(url, {
        method: 'GET',
      });

      if (!response.ok) {
        console.error('Failed to get languages');
        throw new Error('Failed to get languages');
      }

      return response.json();
    } catch (error) {
      console.error('Error getting languages:', error);
      throw error;
    }
  }

  async getTranslations(lang: string): Promise<Record<string, string>> {
    try {
      const url = `${this.baseUrl}Config/Translations?lang=${encodeURIComponent(lang)}&key=`;
      const response = await fetch(url, {
        method: 'GET',
      });

      if (!response.ok) {
        console.error('Failed to get translations');
        throw new Error('Failed to get translations');
      }

      const data: { translations: Record<string, string> } = await response.json();
      return data.translations;
    } catch (error) {
      console.error('Error getting translations:', error);
      throw error;
    }
  }
}

export default ConfigService;
